import React, { useEffect, useState } from 'react';
import { 
  XMarkIcon, 
  TagIcon, 
  MagnifyingGlassIcon,
  BriefcaseIcon 
} from '@heroicons/react/24/outline';
import { DocumentCategory } from '../DocumentCategoryAPI';

interface PracticeAreaDocumentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTypes: DocumentCategory[];
  selectedDocumentTypeId: string;
  onDocumentTypeSelect: (documentTypeId: string) => void;
  title?: string;
  loading?: boolean;
}

export const PracticeAreaDocumentTypeModal: React.FC<PracticeAreaDocumentTypeModalProps> = ({
  isOpen,
  onClose,
  documentTypes,
  selectedDocumentTypeId,
  onDocumentTypeSelect,
  title = "Select Document Type",
  loading = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setIsClosing(false);
    setHasEntered(false);
    const frame = window.requestAnimationFrame(() => setHasEntered(true));

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  // Filter document types based on search
  const filteredDocumentTypes = React.useMemo(() => {
    const activeCategories = documentTypes.filter(category => category.is_active !== false);
    if (!searchQuery.trim()) {
      return activeCategories;
    }

    const query = searchQuery.toLowerCase();
    return activeCategories.filter(docType => 
      docType.name.toLowerCase().includes(query) ||
      docType.category_type?.toLowerCase().includes(query) ||
      docType.practice_areas?.some(area => area.toLowerCase().includes(query))
    );
  }, [documentTypes, searchQuery]);

  // Group assignable document types under their category families.
  const groupedDocumentTypes = React.useMemo(() => {
    const byId = new Map(documentTypes.map(category => [category.id, category]));
    const roots = filteredDocumentTypes
      .filter(category => !category.parent_id)
      .sort((a, b) => (a.display_order ?? a.sort_order ?? 0) - (b.display_order ?? b.sort_order ?? 0) || a.name.localeCompare(b.name));
    const childrenByRoot: Record<string, DocumentCategory[]> = {};

    filteredDocumentTypes
      .filter(category => category.parent_id)
      .forEach(category => {
        const rootId = category.parent_id || 'other';
        if (!childrenByRoot[rootId]) childrenByRoot[rootId] = [];
        childrenByRoot[rootId].push(category);
      });

    Object.values(childrenByRoot).forEach(children => {
      children.sort((a, b) => (a.display_order ?? a.sort_order ?? 0) - (b.display_order ?? b.sort_order ?? 0) || a.name.localeCompare(b.name));
    });

    const rootIds = new Set(roots.map(root => root.id));
    Object.keys(childrenByRoot).forEach(rootId => {
      if (!rootIds.has(rootId)) {
        const parent = byId.get(rootId);
        if (parent) roots.push(parent);
      }
    });

    return roots.map(root => ({
      root,
      children: childrenByRoot[root.id] || [],
    }));
  }, [documentTypes, filteredDocumentTypes]);

  const requestClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    window.setTimeout(() => {
      onClose();
      setSearchQuery('');
      setIsClosing(false);
      setHasEntered(false);
    }, 180);
  };

  const handleDocumentTypeSelect = (documentTypeId: string) => {
    onDocumentTypeSelect(documentTypeId);
    requestClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-200 ease-out ${
          isClosing || !hasEntered ? 'bg-opacity-0' : 'bg-opacity-50'
        }`}
        onClick={requestClose}
      />

      {/* Modal */}
      <div
        className={`relative flex max-h-[min(86vh,48rem)] w-[min(92vw,52rem)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-200 ease-out dark:bg-gray-800 ${
          isClosing || !hasEntered
            ? 'translate-y-3 scale-95 opacity-0'
            : 'translate-y-0 scale-100 opacity-100'
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 px-5 py-4 dark:border-gray-700 sm:px-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {title}
            </h3>
            <button
              onClick={requestClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search document types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
              disabled={loading}
            />
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 py-4 sm:px-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading document types...</p>
            </div>
          ) : (
            <>
              {/* Clear Selection */}
              <button
                onClick={() => handleDocumentTypeSelect('')}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center gap-2 mb-3"
              >
                <TagIcon className="h-4 w-4 text-gray-400" />
                <span>No Type Set</span>
                {!selectedDocumentTypeId && <span className="ml-auto text-lex-accent font-medium">✓</span>}
              </button>

              {/* Document Types */}
              <div className="space-y-4">
                {groupedDocumentTypes.map(({ root, children }) => {
                  const options = children.length > 0 ? children : [root];
                  return (
                    <section key={root.id}>
                      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <BriefcaseIcon className="h-4 w-4 flex-shrink-0 text-lex-accent" />
                        <span className="min-w-0 truncate">{root.name}</span>
                        {root.practice_areas?.length ? (
                          <span className="max-w-full rounded-full bg-gray-100 px-2 py-0.5 text-[10px] normal-case text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                            {root.practice_areas.slice(0, 2).join(', ')}
                          </span>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {options.map(docType => (
                    <button
                      key={docType.id}
                      onClick={() => handleDocumentTypeSelect(docType.id)}
                      className={`min-w-0 w-full px-3 py-3 text-left text-sm rounded-lg border transition-all duration-150 hover:shadow-sm ${
                        selectedDocumentTypeId === docType.id 
                          ? 'bg-lex-accent text-lex-accent-contrast border-lex-accent shadow-md'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-lex-accent'
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <BriefcaseIcon className={`h-4 w-4 ${
                          selectedDocumentTypeId === docType.id ? 'text-white' : 'text-lex-accent'
                        }`} />
                        <div className="min-w-0 flex-1">
                          <div className={`font-medium ${
                            selectedDocumentTypeId === docType.id ? 'text-white' : 'text-gray-900 dark:text-white'
                          }`}>
                            {docType.name}
                          </div>
                          <div className={`text-xs ${
                            selectedDocumentTypeId === docType.id 
                              ? 'text-lex-accent-contrast/80'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {[docType.practice_areas?.join(', '), docType.jurisdictions?.join(', ')]
                              .filter(Boolean)
                              .join(' • ') || 'Firm document taxonomy'}
                          </div>
                        </div>
                        {selectedDocumentTypeId === docType.id && (
                          <span className="flex-shrink-0 text-white font-medium">✓</span>
                        )}
                      </div>
                    </button>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>

              {/* Empty State */}
              {filteredDocumentTypes.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {searchQuery ? 'No document types match your search' : 'No document types available'}
                  {!searchQuery && (
                    <p className="text-xs mt-2">Configure practice areas in Admin → Firm Settings</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
